import { css, StyleSheet } from 'aphrodite';
import { type FC, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
	createRawRichTextContentStateWithHtmlStringValue,
	getContentHtmlStringValueFromRawRichTextContentState,
	type IAutomationTemplateReference,
	type ResourceVisibility,
} from '../../../extViewmodels';
import { replaceTokenWithActualFirstNameString } from '../../../models/UiUtils';
import { useStartAutomationForContactMutation } from '../../../queries/Automation';
import { useTemplate } from '../../../queries/Templates/useTemplate';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { Input } from '../../../web/components-v2/Input';
import { THEME } from '../../../web/components-v2/Theme/theme';
import type { FocusedLeadContact } from '../data';
import { useSendEmail } from '../data/useSendEmail';
import { NoteEditor } from '../wrapup/NoteEditor';
import { AdvancedSettingsPopover } from './AdvancedSettingsPopover';
import { CcField } from './CcField';
import { EmailPreviewDialog } from './EmailPreviewDialog';
import { SignatureSelect } from './SignatureSelect';
import { TemplateAutomationSelect } from './TemplateAutomationSelect';
import { type ComposerSelection } from './types';
import { isValidEmail } from './validation';

/**
 * Focused-view email composer.
 *
 * A modern components-v2 / react-query replacement for the legacy MobX
 * `EmailEditor` modal (`aida/components/EmailEditor/index.tsx`). Supports the two
 * legacy modes via a single top selector:
 *   - Template / Blank → full composer (To, CC, Subject, rich body, signature,
 *     advanced settings, Preview) → "Send Email" via `useSendEmail`.
 *   - Automation → composer collapses to To + "Start Automation"; selecting
 *     enrolls the contact via `useStartAutomationForContactMutation`.
 *
 * The server resolves merge fields + applies the signature at send time.
 */
export type ComposeEmailDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Lead/company id for the send + post-send lead refresh. */
	companyId: string | undefined;
	/** Contact being emailed; seeds the To field. */
	contact: FocusedLeadContact | undefined;
	/** Fired after a successful send or automation start (e.g. to advance UI). */
	onSent?: () => void;
};

/** Cheap "has any visible text" check on the editor's HTML. */
const hasText = (html: string) =>
	html
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/g, ' ')
		.trim().length > 0;

export const ComposeEmailDialog: FC<ComposeEmailDialogProps> = ({ open, onOpenChange, companyId, contact, onSent }) => {
	const sendEmail = useSendEmail();
	const [to, setTo] = useState('');
	const [cc, setCc] = useState<string[]>([]);
	const [subject, setSubject] = useState('');
	const [bodyHtml, setBodyHtml] = useState('');
	const [selection, setSelection] = useState<ComposerSelection>({ kind: 'blank' });
	const [signatureTemplateId, setSignatureTemplateId] = useState('default');
	const [saveAsNote, setSaveAsNote] = useState(true);
	const [noteVisibility, setNoteVisibility] = useState<ResourceVisibility>('all');
	const [showPreview, setShowPreview] = useState(false);
	// Bumped to force the uncontrolled editor to remount with new initial content
	// (on open-reset and whenever a template loads).
	const [editorKey, setEditorKey] = useState(0);

	const isAutomation = selection.kind === 'automation';

	// Reset the form only on the closed→open transition — NOT on every contact
	// re-emit. A background lead refresh (e.g. `useSendEmail`'s onSuccess
	// invalidation) re-emits the contact projection while the dialog is open;
	// resetting then would wipe the rep's in-progress draft. `prevOpenRef` gates
	// the reset to the edge while keeping the deps complete for lint.
	const prevOpenRef = useRef(false);
	useEffect(() => {
		const justOpened = open && !prevOpenRef.current;
		prevOpenRef.current = open;
		if (!justOpened) return;
		setTo(contact?.email ?? '');
		setCc([]);
		setSubject('');
		setBodyHtml('');
		setSelection({ kind: 'blank' });
		setSignatureTemplateId('default');
		setSaveAsNote(true);
		setNoteVisibility('all');
		setShowPreview(false);
		setEditorKey(k => k + 1);
	}, [open, contact?.email, contact?.id]);

	// Load the selected email template's subject + body into the composer.
	const templateId = selection.kind === 'template' ? selection.id : '';
	const templateQuery = useTemplate({ templateId, enabled: selection.kind === 'template' });
	// Apply each template's content at most once — without this, a background
	// contact re-emit (firstName identity flip) re-runs the effect and overwrites
	// the rep's edits with the templated content.
	const lastAppliedTemplateRef = useRef<string | null>(null);
	useEffect(() => {
		if (selection.kind !== 'template') {
			lastAppliedTemplateRef.current = null;
			return;
		}
		const template = templateQuery.data;
		if (!template) return;
		if (lastAppliedTemplateRef.current === templateId) return;
		lastAppliedTemplateRef.current = templateId;
		setSubject(template.subject ?? '');
		// Resolve the first-name token client-side for display (parity with the
		// legacy editor); remaining merge fields resolve server-side at send.
		const content = template.content
			? replaceTokenWithActualFirstNameString(template.content, contact?.firstName ?? '')
			: undefined;
		setBodyHtml(content ? getContentHtmlStringValueFromRawRichTextContentState(content) : '');
		setEditorKey(k => k + 1);
	}, [selection.kind, templateId, templateQuery.data, contact?.firstName]);

	const startAutomation = useStartAutomationForContactMutation({
		onSuccess: () => {
			toast.success('Automation started');
			onOpenChange(false);
			onSent?.();
		},
		onError: error => {
			toast.error(error?.systemMessage || 'Failed to start automation');
		},
	});

	const canSend =
		!isAutomation &&
		!!companyId &&
		!!contact &&
		isValidEmail(to) &&
		subject.trim().length > 0 &&
		hasText(bodyHtml) &&
		!sendEmail.isPending;

	const canStartAutomation = isAutomation && !!contact && selection.kind === 'automation' && !startAutomation.isPending;

	const handleSend = async () => {
		if (!canSend || !companyId || !contact) return;
		try {
			await sendEmail.mutateAsync({
				companyId,
				contactId: contact.id,
				toEmail: to.trim(),
				subject: subject.trim(),
				bodyHtml,
				cc,
				signatureTemplateId,
				saveAsNote,
				noteVisibility,
			});
			toast.success('Email sent');
			onOpenChange(false);
			onSent?.();
		} catch {
			// useSendEmail's onError already toasts; keep the dialog open so the
			// rep can retry without losing their draft.
		}
	};

	const handleStartAutomation = () => {
		if (!canStartAutomation || selection.kind !== 'automation' || !contact) return;
		startAutomation.mutate({
			// The endpoint reads only `template.id`; the rest of the reference shape
			// isn't required for enrollment (see useStartAutomationForContact).
			template: { id: selection.id, name: selection.name } as unknown as IAutomationTemplateReference,
			contactId: contact.id,
			start: true,
		});
	};

	const busy = sendEmail.isPending || startAutomation.isPending;

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogPopup styles={[styles.popup]}>
					<DialogHeader>
						<div className={css(styles.headerRow)}>
							<DialogTitle>{contact ? `Email ${contact.name}` : 'Email'}</DialogTitle>
							<div className={css(styles.headerSelect)}>
								<TemplateAutomationSelect selection={selection} onSelectionChange={setSelection} disabled={busy} />
							</div>
						</div>
					</DialogHeader>
					<DialogBody>
						<div className={css(styles.fieldRow)}>
							<label htmlFor='focused-compose-to' className={css(styles.labelInline)}>
								To
							</label>
							<div className={css(styles.fieldControl)}>
								<Input
									id='focused-compose-to'
									type='email'
									value={to}
									onChange={e => setTo(e.target.value)}
									placeholder='name@example.com'
									disabled={busy}
									styles={[styles.fullWidth]}
								/>
							</div>
						</div>

						{/* Template / Blank mode: the full email composer. */}
						{!isAutomation && (
							<>
								<div className={css(styles.fieldRow)}>
									<label className={css(styles.labelInline)}>Cc</label>
									<div className={css(styles.fieldControl)}>
										<CcField value={cc} onChange={setCc} disabled={busy} />
									</div>
								</div>
								<div className={css(styles.fieldRow)}>
									<label htmlFor='focused-compose-subject' className={css(styles.labelInline)}>
										Subject
									</label>
									<div className={css(styles.fieldControl)}>
										<Input
											id='focused-compose-subject'
											value={subject}
											onChange={e => setSubject(e.target.value)}
											placeholder='Subject'
											disabled={busy}
											styles={[styles.fullWidth]}
										/>
									</div>
								</div>
								<div className={css(styles.bodyField)}>
									<label className={css(styles.label)}>Message</label>
									{/* Editor + the signature / advanced-settings bar share one
									    bordered box (the editor draws no border of its own). */}
									<div className={css(styles.composerBox)}>
										{/* Remounts per contact + per template load so the uncontrolled
										    editor picks up freshly-loaded content. */}
										<NoteEditor
											key={`${contact?.id ?? 'none'}-${editorKey}`}
											rich
											bordered={false}
											value={bodyHtml}
											onChange={setBodyHtml}
											placeholder='Write your message…'
											disabled={busy}
											minHeight={170}
										/>
										<div className={css(styles.bottomBar)}>
											<div className={css(styles.barCellLeft)}>
												<SignatureSelect
													value={signatureTemplateId}
													onChange={setSignatureTemplateId}
													disabled={busy}
													triggerStyles={styles.barTrigger}
												/>
											</div>
											<div className={css(styles.barCellRight)}>
												<AdvancedSettingsPopover
													saveAsNote={saveAsNote}
													onSaveAsNoteChange={setSaveAsNote}
													noteVisibility={noteVisibility}
													onNoteVisibilityChange={setNoteVisibility}
													disabled={busy}
												/>
											</div>
										</div>
									</div>
								</div>
							</>
						)}
					</DialogBody>
					<DialogFooter>
						<Button variant='outline' onClick={() => onOpenChange(false)} disabled={busy}>
							Cancel
						</Button>
						{!isAutomation && (
							<Button variant='outline' onClick={() => setShowPreview(true)} disabled={busy || !hasText(bodyHtml)}>
								Preview
							</Button>
						)}
						{isAutomation ? (
							<Button variant='primary' onClick={handleStartAutomation} disabled={!canStartAutomation}>
								{startAutomation.isPending ? 'Starting…' : 'Start Automation'}
							</Button>
						) : (
							<Button variant='primary' onClick={handleSend} disabled={!canSend}>
								{sendEmail.isPending ? 'Sending…' : 'Send Email'}
							</Button>
						)}
					</DialogFooter>
				</DialogPopup>
			</Dialog>

			<EmailPreviewDialog
				open={showPreview}
				onOpenChange={setShowPreview}
				content={createRawRichTextContentStateWithHtmlStringValue(bodyHtml ?? '')}
				signatureTemplateId={signatureTemplateId === 'default' ? undefined : signatureTemplateId}
			/>
		</>
	);
};

const styles = StyleSheet.create({
	popup: {
		// Wider so the editor toolbar fits without collapsing controls into the
		// "…" overflow menu (which renders behind the modal).
		maxWidth: 820,
		width: '92vw',
	},
	// Title (left) + template/automation selector (right) on the header row.
	// `paddingRight` keeps the selector clear of the Dialog's absolute close (×).
	headerRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.md,
		width: '100%',
		paddingRight: THEME.spacing.lg,
	},
	headerSelect: {
		width: 190,
		flexShrink: 0,
	},
	// Inline field: label to the left of a flexible control.
	fieldRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		marginBottom: THEME.spacing.sm,
	},
	labelInline: {
		width: 52,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
	},
	fieldControl: {
		flex: 1,
		minWidth: 0,
	},
	fullWidth: {
		width: '100%',
	},
	bodyField: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		marginBottom: THEME.spacing.sm,
	},
	// Editor + bottom toolbar as one bordered box (the editor is borderless).
	// Uses THEME tokens (global CSS vars) rather than the focused-view `--*` vars,
	// which don't resolve in the portaled Dialog subtree.
	composerBox: {
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.background.light,
	},
	// Full-width strip under the editor, split into two cells by a divider.
	bottomBar: {
		display: 'flex',
		alignItems: 'stretch',
		borderTop: `1px solid ${THEME.colors.border.main}`,
	},
	barCellLeft: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		borderRight: `1px solid ${THEME.colors.border.main}`,
		minWidth: 0,
	},
	barCellRight: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		paddingLeft: THEME.spacing.sm,
	},
	// Make the signature trigger fill its cell and blend into the bar (no box).
	barTrigger: {
		width: '100%',
		border: 'none',
		borderRadius: 0,
		height: 42,
	},
	label: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
	},
});
